import type { CrmContactKind, CrmDealDto, CrmDealProductKind } from '@upme/contracts';
import {
  isBotBlocked,
  isBotNotStarted,
  resolveOrganizerRoleStatus,
  resolveParticipantRoleStatus,
} from './role-activity';

/** Роли на компактной карточке CRM (не adminRoles). */
export type CrmCardRoleKind = 'organizer' | 'participant' | 'specialist' | 'client';

export type CrmRoleActivityStatus = 'outside' | 'guest' | 'active';

export type CrmCardRole = {
  kind: CrmCardRoleKind;
  status: CrmRoleActivityStatus;
};

export function crmContactDisplayName(contact: CrmDealDto['contact']): string {
  const full = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (contact.username) return contact.username;
  return contact.contactLabel.replace(/^@/, '');
}

/** Specialist / client — нет отдельных критериев Active в продукте. */
function resolveGenericGuestStatus(contact: CrmDealDto['contact']): CrmRoleActivityStatus {
  if (isBotBlocked(contact)) return 'outside';
  return 'guest';
}

function pushRole(roles: CrmCardRole[], kind: CrmCardRoleKind, status: CrmRoleActivityStatus) {
  if (roles.some((r) => r.kind === kind)) return;
  roles.push({ kind, status });
}

function productImpliesClient(productKind: CrmDealProductKind): boolean {
  return productKind === 'pro' || productKind === 'partner' || productKind === 'investor';
}

/**
 * Роли для одной карточки сделки. Несколько ролей возможны (организатор + участник).
 * Статусы не выводятся из цвета тега — только из данных контакта.
 */
export function deriveCrmCardRoles(
  deal: Pick<CrmDealDto, 'contact' | 'contactKind' | 'productKind'>,
): CrmCardRole[] {
  const { contact, contactKind, productKind } = deal;
  const roles: CrmCardRole[] = [];

  if (contactKind === 'organizer') {
    pushRole(roles, 'organizer', resolveOrganizerRoleStatus(contact));
  }
  if (contactKind === 'participant') {
    pushRole(roles, 'participant', resolveParticipantRoleStatus(contact));
  }
  if (contactKind === 'lead') {
    pushRole(roles, 'participant', resolveParticipantRoleStatus(contact));
  }

  if (contact.meetingsCount > 0 && contactKind === 'organizer') {
    pushRole(roles, 'participant', resolveParticipantRoleStatus(contact));
  }

  if (productKind === 'service') {
    pushRole(roles, 'specialist', resolveGenericGuestStatus(contact));
  }
  if (productImpliesClient(productKind)) {
    pushRole(roles, 'client', resolveGenericGuestStatus(contact));
  }

  if (roles.length === 0) {
    pushRole(roles, 'participant', resolveParticipantRoleStatus(contact));
  }

  return roles;
}

export const CRM_CARD_ROLE_LABEL_RU: Record<CrmCardRoleKind, string> = {
  organizer: 'Организатор',
  participant: 'Участник',
  specialist: 'Специалист',
  client: 'Клиент',
};

export const CRM_ROLE_ACTIVITY_LABEL_RU: Record<CrmRoleActivityStatus, string> = {
  outside: 'Вне UpMe',
  guest: 'Гость',
  active: 'Активный',
};

export function crmRoleActivityTooltip(
  kind: CrmCardRoleKind,
  status: CrmRoleActivityStatus,
  contact: CrmDealDto['contact'],
): string {
  const role = CRM_CARD_ROLE_LABEL_RU[kind];
  const statusLabel = CRM_ROLE_ACTIVITY_LABEL_RU[status];
  if (kind === 'participant' && status === 'active') {
    return `${statusLabel} — есть прошедшее участие (CONFIRMED EVENT|MEETING, как в Connection Card).`;
  }
  if (kind === 'participant' && status === 'guest' && isBotNotStarted(contact)) {
    return `${statusLabel} — аккаунт есть, Start в боте не нажат; посещений пока нет.`;
  }
  if (kind === 'participant' && status === 'guest') {
    return `${statusLabel} — в системе, но ещё нет прошедших посещений (${role}).`;
  }
  if (kind === 'organizer' && status === 'active') {
    return `${statusLabel} — прошлый ивент с QR check-in (билет CHECKED_IN) или групповым фото с тегами.`;
  }
  if (kind === 'organizer' && status === 'guest') {
    return `${statusLabel} — организатор без подтверждённого прошедшего ивента (${role}).`;
  }
  if (status === 'outside') {
    return `${statusLabel} — бот заблокирован, рассылки недоступны (${role}).`;
  }
  if (kind === 'specialist' || kind === 'client') {
    return `${statusLabel} — критерии «Активный» для роли «${role}» в продукте не зафиксированы.`;
  }
  return `${statusLabel} (${role}).`;
}
